import { useEffect, useState } from "react";
import { Activity, AlertCircle, MapPin } from "lucide-react";

/**
 * GpsFreshnessBadge — Phase 42 (D)
 *
 * Mostra al cliente lo stato della posizione del driver: quanto tempo fa
 * è stato ricevuto l'ultimo ping GPS. Tre stati visibili:
 *  - "fresh"    (<= 30s)    → verde: "Posizione live"
 *  - "stale"    (30s–2min)  → ambra:  "Ultimo aggiornamento Xs fa"
 *  - "cold"     (> 2min)    → giallo: "Posizione non aggiornata · ultimo segnale X min fa"
 *  - "unknown"  (nessuna)   → grigio: "In attesa del primo segnale GPS"
 *
 * - Niente promesse di tracking perfetto; trasparenza esplicita.
 * - Non rende nulla se non c'è tracking attivo.
 */
const FRESH_S = 30;
const STALE_S = 120;

function formatAgo(seconds) {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s fa`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min fa`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h fa`;
}

export default function GpsFreshnessBadge({ trackingActive, lastLocationAtIso, className = "" }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!trackingActive) return;
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, [trackingActive]);

  if (!trackingActive) return null;

  const lastTs = lastLocationAtIso ? new Date(lastLocationAtIso).getTime() : null;
  const ageSec = lastTs ? (now - lastTs) / 1000 : null;

  let state, label, message, Icon, tone;
  if (ageSec === null) {
    state = "unknown";
    label = "In attesa del primo segnale GPS";
    message = "La posizione sarà mostrata quando sarà disponibile un segnale GPS.";
    Icon = MapPin;
    tone = "bg-zinc-50 ring-zinc-200 text-zinc-800";
  } else if (ageSec <= FRESH_S) {
    state = "fresh";
    label = "Posizione live";
    message = `Aggiornata ${formatAgo(ageSec)}`;
    Icon = Activity;
    tone = "bg-emerald-50 ring-emerald-200 text-emerald-800";
  } else if (ageSec <= STALE_S) {
    state = "stale";
    label = `Ultimo aggiornamento ${formatAgo(ageSec)}`;
    message = "Stiamo aspettando il prossimo segnale GPS.";
    Icon = AlertCircle;
    tone = "bg-amber-50 ring-amber-200 text-amber-900";
  } else {
    state = "cold";
    label = `Posizione non aggiornata · ultimo segnale ${formatAgo(ageSec)}`;
    message = "Il tracking live può interrompersi quando il driver usa un navigatore esterno. Gli aggiornamenti riprendono quando il dispositivo invia un nuovo segnale.";
    Icon = AlertCircle;
    tone = "bg-amber-50 ring-amber-300 text-amber-900";
  }

  return (
    <div
      className={`rounded-2xl ring-1 p-3 sm:p-3.5 flex items-start gap-2.5 ${tone} ${className}`}
      data-testid="gps-freshness-badge"
      data-state={state}
    >
      <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${state === "fresh" ? "animate-pulse" : ""}`} />
      <div className="flex-1 text-xs">
        <p className="font-semibold" data-testid="gps-freshness-label">{label}</p>
        <p className="opacity-80 mt-0.5">{message}</p>
      </div>
    </div>
  );
}
